import { useEffect, useRef } from 'react';
import { matchShortcut, type ShortcutAction } from '../keyboardShortcuts';

/** What each shortcut does on this screen. An action left out is not taken, and its key is left alone. */
export type ShortcutHandlers = Partial<Record<ShortcutAction, () => void>>;

/**
 * The file browser's keyboard shortcuts, for any grid that has a selection —
 * the Files browser and the Shared view (#1692).
 *
 * The chord → action map is `matchShortcut` (pure, unit-tested); this only
 * listens. A screen opts into the actions it can honour: the Shared view has
 * select-all and clear but nothing to cut or paste into, so Ctrl+X/V there are
 * not intercepted at all.
 *
 * One document listener for the component's lifetime: the handlers are read
 * through a ref, so a re-render with new closures does not re-register it.
 */
export function useBrowserShortcuts(handlers: ShortcutHandlers): void {
  const handlersRef = useRef(handlers);
  useEffect(() => {
    handlersRef.current = handlers;
  });

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement | null;
      const inEditableTarget =
        !!target &&
        (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable);
      const action = matchShortcut({
        key: e.key,
        ctrlKey: e.ctrlKey,
        metaKey: e.metaKey,
        inEditableTarget,
      });
      if (!action) return;
      const handler = handlersRef.current[action];
      if (!handler) return;
      e.preventDefault();
      handler();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, []);
}
